import { useQuery } from '@apollo/client/react';
import { useState } from 'react';
import { BoxArrowUpRight } from 'react-bootstrap-icons';

import { describePaymentMethodBrand } from '@repo/common';
import { BillingPaymentMethodsDocument, type BillingPaymentMethodsQuery, DataTestIds } from '@repo/frontend-shared';
import { SDSButton } from '@repo/sds/SDSButton';
import { SDSFlexbox } from '@repo/sds/SDSFlexbox';
import { SDSText } from '@repo/sds/SDSText';
import { VisaMarkIcon } from '@repo/ui/assets/icons/paymentIcons/visa-mark-icon';
import { SettingsCard } from '@repo/ui/components/elements/settings-card/settings-card';

import { formatProviderStatus } from './billing-format';
import { ContinueToStripeDialog } from './continue-to-stripe-dialog';
import { StripeNotice } from './payment-summary';

type PaymentMethod = BillingPaymentMethodsQuery['myPaymentMethods'][number];

interface PaymentMethodsSectionProps {
  isPortalLoading: boolean;
  hasBillingPlan?: boolean;
  /** Whether this account still has instalments left to charge. */
  hasScheduledPayments?: boolean;
  onOpenPortal(): void;
}

function BrandMark({ brand }: { brand: string | null | undefined }) {
  // Only Visa has a mark in the codebase today; every other brand falls back
  // to its name as text rather than a guessed icon.
  if (brand?.toLowerCase() === 'visa') {
    return <VisaMarkIcon className="BillingPaymentMethods__visa" />;
  }
  return <SDSText type="body-medium-bold" text={describePaymentMethodBrand(brand) ?? 'Card'} />;
}

function PaymentMethodLabel({ paymentMethod }: { paymentMethod: PaymentMethod }) {
  // The same wording the refund dialog uses for "where the money goes back
  // to" — the two name the same card and must read as one.
  const brandLabel = describePaymentMethodBrand(paymentMethod.brand) ?? formatProviderStatus(paymentMethod.type);
  const last4Label = paymentMethod.last4 ? ` ending ${paymentMethod.last4}` : '';
  const expiryLabel =
    paymentMethod.expiryMonth && paymentMethod.expiryYear
      ? `Expiry: ${String(paymentMethod.expiryMonth).padStart(2, '0')}/${paymentMethod.expiryYear}`
      : undefined;

  return (
    <SDSFlexbox
      direction="row"
      gap="text-large"
      alignItems="center"
      dataTestId={DataTestIds.BILLING.PAYMENT_METHOD_ROW}
    >
      {/* A fixed slot, the width of the frame's Visa mark, so every card's
          label starts at the same x whatever its brand is drawn as. */}
      <SDSFlexbox className="BillingPaymentMethods__mark" direction="row" alignItems="center">
        <BrandMark brand={paymentMethod.brand} />
      </SDSFlexbox>
      <SDSFlexbox direction="column" gap="no-design-4" alignItems="flex-start">
        <SDSText type="body-medium" text={`${brandLabel}${last4Label}`} />
        {/* Name then expiry on one line, 12px apart, as the frame sets them.
            Stripe does not require a name on a card, so the line falls back
            to the expiry alone. */}
        {paymentMethod.cardholderName || expiryLabel ? (
          <SDSFlexbox direction="row" gap="text-large" alignItems="center" wrap="wrap">
            {paymentMethod.cardholderName ? (
              <SDSText
                type="body-small"
                text={paymentMethod.cardholderName}
                dataTestId={DataTestIds.BILLING.PAYMENT_METHOD_CARDHOLDER}
              />
            ) : null}
            {expiryLabel ? <SDSText type="body-small" text={expiryLabel} /> : null}
          </SDSFlexbox>
        ) : null}
      </SDSFlexbox>
    </SDSFlexbox>
  );
}

function ChangePaymentMethodButton({
  isPortalLoading,
  hasScheduledPayments = false,
  onOpenPortal,
}: PaymentMethodsSectionProps) {
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  return (
    <SDSFlexbox direction="column" gap="text-small" alignItems="flex-end">
      <SDSButton
        className="BillingPaymentMethods__action"
        text="Change payment method"
        mode="outline"
        size="medium"
        rightIcon={<BoxArrowUpRight />}
        onClick={() => {
          setIsConfirmOpen(true);
        }}
        dataTestId={DataTestIds.BILLING.CHANGE_PAYMENT_METHOD}
      />
      {isConfirmOpen ? (
        <ContinueToStripeDialog
          isOpen
          hasScheduledPayments={hasScheduledPayments}
          isPortalLoading={isPortalLoading}
          onConfirm={onOpenPortal}
          onClose={() => {
            setIsConfirmOpen(false);
          }}
        />
      ) : null}
      <StripeNotice text="Saved securely for future purchases in" />
    </SDSFlexbox>
  );
}

/**
 * The account's saved payment methods, with the Stripe portal as the only
 * place to add or change one — there is no local mutation for it.
 */
export function PaymentMethodsSection({
  isPortalLoading,
  hasBillingPlan,
  hasScheduledPayments,
  onOpenPortal,
}: PaymentMethodsSectionProps) {
  const { data, loading, error } = useQuery<BillingPaymentMethodsQuery>(BillingPaymentMethodsDocument, {
    fetchPolicy: 'cache-and-network',
  });
  const paymentMethods = data?.myPaymentMethods ?? [];

  return (
    <SettingsCard direction="column" dataTestId={DataTestIds.BILLING.PAYMENT_METHODS_SECTION}>
      <SDSFlexbox direction="column" gap="content-default" fullWidth>
        <SDSText text="Payment methods" type="h5" />
        <PaymentMethodsContent
          paymentMethods={paymentMethods}
          isLoading={loading && !data}
          hasError={Boolean(error)}
          isPortalLoading={isPortalLoading}
          hasBillingPlan={hasBillingPlan}
          hasScheduledPayments={hasScheduledPayments}
          onOpenPortal={onOpenPortal}
        />
      </SDSFlexbox>
    </SettingsCard>
  );
}

function PaymentMethodsContent({
  paymentMethods,
  isLoading,
  hasError,
  isPortalLoading,
  hasBillingPlan,
  hasScheduledPayments,
  onOpenPortal,
}: PaymentMethodsSectionProps & {
  paymentMethods: readonly PaymentMethod[];
  isLoading: boolean;
  hasError: boolean;
}) {
  if (isLoading) {
    return (
      <SDSText
        type="body-medium"
        text="Loading your payment methods…"
        color="text-secondary"
        dataTestId={DataTestIds.BILLING.PAYMENT_METHODS_LOADING_STATE}
      />
    );
  }

  // Error before not-found: a failed read has not established there is
  // nothing saved.
  if (hasError) {
    return (
      <SDSText
        type="body-medium"
        text="Your payment methods could not be loaded right now."
        color="alert"
        dataTestId={DataTestIds.BILLING.PAYMENT_METHODS_ERROR_STATE}
      />
    );
  }

  if (paymentMethods.length === 0) {
    return (
      <SDSFlexbox direction="column" gap="text-medium" alignItems="flex-start">
        <SDSText
          type="body-medium"
          text="No saved payment methods are on record for this account yet."
          color="text-secondary"
          dataTestId={DataTestIds.BILLING.PAYMENT_METHODS_EMPTY_STATE}
        />
        {hasBillingPlan ? (
          <ChangePaymentMethodButton
            isPortalLoading={isPortalLoading}
            hasScheduledPayments={hasScheduledPayments}
            onOpenPortal={onOpenPortal}
          />
        ) : null}
      </SDSFlexbox>
    );
  }

  return (
    <SDSFlexbox
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      gap="content-default"
      wrap="wrap"
      fullWidth
    >
      <SDSFlexbox direction="column" gap="text-small">
        {paymentMethods.map(paymentMethod => (
          <PaymentMethodLabel key={paymentMethod.id} paymentMethod={paymentMethod} />
        ))}
      </SDSFlexbox>
      <ChangePaymentMethodButton
        isPortalLoading={isPortalLoading}
        hasScheduledPayments={hasScheduledPayments}
        onOpenPortal={onOpenPortal}
      />
    </SDSFlexbox>
  );
}
